<template>
  <AppNavbar />
  <div class="vx-admin">

    <div class="d-flex align-items-end justify-content-between mb-3">
      <div>
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span><span>Esquemas de cobro</span>
        </div>
        <h1 class="vx-titulo">Esquemas de cobro</h1>
      </div>
      <button v-if="puedeAdministrar" class="vx-btn vx-btn-primario" @click="alternarAlta">
        <i class="bi" :class="mostrarAlta ? 'bi-x-lg' : 'bi-plus-lg'"></i>
        {{ mostrarAlta ? 'Cancelar' : 'Nuevo esquema' }}
      </button>
    </div>

    <!-- Alta. Panel desplegable y no pantalla aparte: un esquema son seis
         campos, y sacar al operador del listado para pedirle seis campos es
         hacerle perder de vista lo que ya tiene configurado. -->
    <div v-if="mostrarAlta" class="vx-panel mb-3">
      <div class="vx-panel-cabecera">
        <h2 class="vx-panel-titulo">Nuevo esquema</h2>
      </div>
      <div class="p-3">
        <div class="row g-3">
          <div class="col-md-6">
            <label class="vx-etiqueta" for="e-nombre">Nombre</label>
            <input
              id="e-nombre" v-model.trim="form.nombre" class="vx-campo"
              placeholder="Mensualidad 2026"
              :class="{ 'vx-campo-error': errorDe('nombre') }"
            >
            <div v-if="errorDe('nombre')" class="vx-error">{{ errorDe('nombre') }}</div>
          </div>
          <div class="col-md-6">
            <label class="vx-etiqueta" for="e-descripcion">Descripción</label>
            <input
              id="e-descripcion" v-model.trim="form.descripcion" class="vx-campo"
              placeholder="Opcional"
            >
          </div>
        </div>

        <!-- Los dos modos. Excluyentes en la base (`cr_esquemas_modo_coherente`),
             así que se eligen y la pantalla muestra SÓLO los campos de ese modo:
             pedirle a alguien que cobra cada 30 días el "día del mes" sería
             reclamarle un campo que su esquema no tiene. -->
        <div class="mt-3">
          <label class="vx-etiqueta">Cada cuánto se cobra</label>
          <div class="d-flex gap-2 flex-wrap">
            <button
              v-for="m in MODOS" :key="m.valor"
              :id="`modo-${m.valor.toLowerCase()}`"
              class="text-start p-3 rounded flex-grow-1"
              style="min-width: 260px; cursor: pointer; background: transparent"
              :style="estiloModo(m.valor)"
              @click="elegirModo(m.valor)"
            >
              <div style="font-size: 13.5px; font-weight: 600">{{ m.titulo }}</div>
              <div class="vx-ayuda mt-1">{{ m.nota }}</div>
            </button>
          </div>
        </div>

        <div class="row g-3 mt-1">
          <template v-if="form.periodicidad_tipo === 'CALENDARIO'">
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-preset">Periodicidad</label>
              <select
                id="e-preset" v-model="form.periodicidad_preset" class="vx-campo"
                :class="{ 'vx-campo-error': errorDe('periodicidad_preset') }"
              >
                <option v-for="p in PRESETS_PERIODICIDAD" :key="p.valor" :value="p.valor">
                  {{ p.texto }}
                </option>
              </select>
              <div v-if="errorDe('periodicidad_preset')" class="vx-error">{{ errorDe('periodicidad_preset') }}</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-dia">Día de vencimiento</label>
              <input
                id="e-dia" v-model.number="form.dia_vencimiento" class="vx-campo vx-num"
                type="number" min="1" max="31"
                :class="{ 'vx-campo-error': errorDe('dia_vencimiento') }"
              >
              <div v-if="errorDe('dia_vencimiento')" class="vx-error">{{ errorDe('dia_vencimiento') }}</div>
              <div v-else class="vx-ayuda mt-1">Si el mes no tiene ese día, vence el último.</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-inicio">Primer período</label>
              <input
                id="e-inicio" v-model.trim="form.periodo_inicio" class="vx-campo vx-num"
                placeholder="2026-03" maxlength="7"
                :class="{ 'vx-campo-error': errorDe('periodo_inicio') }"
              >
              <div v-if="errorDe('periodo_inicio')" class="vx-error">{{ errorDe('periodo_inicio') }}</div>
              <div v-else class="vx-ayuda mt-1">Mes y año: AAAA-MM.</div>
            </div>
          </template>

          <template v-else>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-dias">Cada cuántos días</label>
              <input
                id="e-dias" v-model.number="form.periodicidad_dias" class="vx-campo vx-num"
                type="number" min="1"
                :class="{ 'vx-campo-error': errorDe('periodicidad_dias') }"
              >
              <div v-if="errorDe('periodicidad_dias')" class="vx-error">{{ errorDe('periodicidad_dias') }}</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-vence-dias">Vence a los</label>
              <input
                id="e-vence-dias" v-model.number="form.dias_hasta_vencimiento" class="vx-campo vx-num"
                type="number" min="0"
                :class="{ 'vx-campo-error': errorDe('dias_hasta_vencimiento') }"
              >
              <div v-if="errorDe('dias_hasta_vencimiento')" class="vx-error">{{ errorDe('dias_hasta_vencimiento') }}</div>
              <div v-else class="vx-ayuda mt-1">Días desde que arranca el período.</div>
            </div>
            <div class="col-md-3">
              <label class="vx-etiqueta" for="e-inicio-dia">Primer período</label>
              <input
                id="e-inicio-dia" v-model.trim="form.periodo_inicio" class="vx-campo vx-num"
                type="date"
                :class="{ 'vx-campo-error': errorDe('periodo_inicio') }"
              >
              <div v-if="errorDe('periodo_inicio')" class="vx-error">{{ errorDe('periodo_inicio') }}</div>
            </div>
          </template>

          <div class="col-md-3">
            <label class="vx-etiqueta" for="e-cantidad">Cantidad de períodos</label>
            <input
              id="e-cantidad" v-model.number="form.cantidad_periodos" class="vx-campo vx-num"
              type="number" min="1"
              :class="{ 'vx-campo-error': errorDe('cantidad_periodos') }"
            >
            <div v-if="errorDe('cantidad_periodos')" class="vx-error">{{ errorDe('cantidad_periodos') }}</div>
          </div>
        </div>

        <!-- Se repite la configuración en una frase, no se dibujan fechas: los
             períodos los calcula el backend con la misma función que usa la
             generación, y aparecen al guardar. Adivinarlos acá con aritmética
             propia sería mostrar una previsualización que puede no coincidir
             con lo que después se genere. -->
        <div class="vx-nota mt-3">
          <i class="bi bi-info-circle"></i>
          <span>{{ resumenConfiguracion }}</span>
        </div>

        <div v-if="error" class="alert alert-danger py-2 px-3 mt-3" style="font-size: 13.5px" role="alert">
          {{ error }}
        </div>

        <div class="d-flex justify-content-end gap-2 mt-3">
          <button class="vx-btn" :disabled="guardando" @click="alternarAlta">Cancelar</button>
          <button class="vx-btn vx-btn-primario" :disabled="guardando" @click="guardar">
            <span v-if="guardando" class="spinner-border spinner-border-sm"></span>
            {{ guardando ? 'Creando…' : 'Crear esquema' }}
          </button>
        </div>
        <div v-if="motivoBloqueo" class="vx-ayuda text-end mt-2" :class="{ 'vx-error': tocado }">
          {{ motivoBloqueo }}
        </div>
      </div>
    </div>

    <div class="vx-panel">
      <div class="d-flex align-items-center gap-2 p-3 border-bottom">
        <label class="vx-ayuda d-flex align-items-center gap-2 mb-0">
          <input v-model="incluirInactivos" type="checkbox" @change="cargar">
          Incluir inactivos
        </label>
        <div class="flex-grow-1"></div>
        <span v-if="!cargando" class="vx-num vx-ayuda">{{ esquemas.length }} esquemas</span>
      </div>

      <div v-if="cargando" class="vx-vacio">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span class="vx-vacio-texto">Cargando…</span>
      </div>

      <div v-else-if="errorListado" class="vx-vacio">
        <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">No se pudo cargar el listado</span>
        <span class="vx-vacio-texto">{{ errorListado }}</span>
        <button class="vx-btn vx-btn-sm mt-1" @click="cargar">Reintentar</button>
      </div>

      <div v-else-if="!esquemas.length" class="vx-vacio">
        <i class="bi bi-diagram-3" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">Todavía no hay esquemas de cobro</span>
        <span class="vx-vacio-texto">
          Un esquema define cada cuánto se cobra, cuántos períodos, cuándo vencen
          y qué conceptos se cobran en cada uno.
        </span>
      </div>

      <table v-else class="vx-tabla">
        <thead>
          <tr>
            <th>Esquema</th>
            <th>Periodicidad</th>
            <th class="vx-col-num">Períodos</th>
            <th>Desde</th>
            <th>Estado</th>
            <th style="width: 40px"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="e in esquemas" :key="e.cr_esquema_id"
            style="cursor: pointer" @click="abrir(e)"
          >
            <td>
              <div class="vx-celda-principal">{{ e.nombre }}</div>
              <div v-if="e.descripcion" class="vx-celda-sub">{{ e.descripcion }}</div>
            </td>
            <td>{{ periodicidadTexto(e) }}</td>
            <td class="vx-col-num vx-num">{{ e.cantidad_periodos }}</td>
            <td class="vx-num">{{ e.periodo_inicio }}</td>
            <td>
              <span class="vx-estado" :class="e.activo ? 'vx-estado-activa' : 'vx-estado-neutro'">
                {{ e.activo ? 'Activo' : 'Inactivo' }}
              </span>
            </td>
            <td class="text-end">
              <i class="bi bi-chevron-right" style="color: var(--vx-texto-4); font-size: 13px"></i>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
// Listado y alta de esquemas de cobro — CR-E2-006.
// SPEC RN-8 · D19 · diseño aprobado 2026-08-31.

import { ref, reactive, computed, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { useNotificationStore } from '@/stores/notifications'
import { hasAnyPermission } from '@/services/authSessionService'
import {
  listarEsquemas, crearEsquema, PRESETS_PERIODICIDAD, presetTexto, periodicidadTexto
} from '@/services/cobranzaService'
import { validarEsquema, primerCampoConError } from '@/services/cobranzaSchemas'

const router = useRouter()
const notificaciones = useNotificationStore()

const MODOS = [
  {
    valor: 'CALENDARIO',
    titulo: 'Anclado al mes',
    nota: 'Mensual, bimestral, trimestral… vence un día fijo del mes'
  },
  {
    valor: 'DIAS',
    titulo: 'Cada N días',
    nota: 'Ciclos de días corridos desde el período anterior'
  }
]

const esquemas = ref([])
const cargando = ref(true)
const errorListado = ref('')
const incluirInactivos = ref(false)
const mostrarAlta = ref(false)
const guardando = ref(false)
const error = ref('')
const tocado = ref(false)

const puedeAdministrar = computed(() => hasAnyPermission(['administrar_esquemas_cobro']))

const ESTADO_INICIAL = {
  nombre: '',
  descripcion: '',
  periodicidad_tipo: 'CALENDARIO',
  periodicidad_preset: 'MENSUAL',
  dia_vencimiento: 10,
  periodicidad_dias: null,
  dias_hasta_vencimiento: null,
  cantidad_periodos: 12,
  periodo_inicio: ''
}

const form = reactive({ ...ESTADO_INICIAL })

// Orden de lectura, no el orden en que zod reporta: el foco tiene que ir al
// primer campo que el operador encontraría leyendo de arriba hacia abajo.
const ORDEN_CAMPOS = [
  'nombre', 'periodicidad_preset', 'periodicidad_dias', 'dia_vencimiento',
  'dias_hasta_vencimiento', 'periodo_inicio', 'cantidad_periodos'
]

const ANCLAS = {
  nombre: '#e-nombre',
  periodicidad_preset: '#e-preset',
  dia_vencimiento: '#e-dia',
  periodicidad_dias: '#e-dias',
  dias_hasta_vencimiento: '#e-vence-dias',
  cantidad_periodos: '#e-cantidad'
}

const erroresTodos = computed(() => validarEsquema(form))
const errores = computed(() => (tocado.value ? erroresTodos.value : {}))
const errorDe = (campo) => errores.value[campo] || ''
const puedeGuardar = computed(() => Object.keys(erroresTodos.value).length === 0)

const motivoBloqueo = computed(() => {
  const primero = primerCampoConError(erroresTodos.value, ORDEN_CAMPOS)
  return primero ? erroresTodos.value[primero] : ''
})

const resumenConfiguracion = computed(() => {
  const n = form.cantidad_periodos || 0
  const cuotas = `${n} ${n === 1 ? 'período' : 'períodos'}`
  if (form.periodicidad_tipo === 'DIAS') {
    return `${cuotas} de ${form.periodicidad_dias || '—'} días, cada uno vence a los ` +
      `${form.dias_hasta_vencimiento ?? '—'} días de arrancar. Los períodos se calculan al guardar.`
  }
  return `${cuotas} ${presetTexto(form.periodicidad_preset).toLowerCase()}, cada uno vence el ` +
    `${form.dia_vencimiento || '—'}. Los períodos se calculan al guardar.`
})

const estiloModo = (v) => form.periodicidad_tipo === v
  ? { border: '1.5px solid var(--vx-acento)', background: 'rgba(7,167,225,0.05)' }
  : { border: '1px solid var(--vx-borde-fuerte)' }

// Cambiar de modo LIMPIA los campos del otro. No es cosmética: la base rechaza
// un esquema que traiga campos de los dos modos (`cr_esquemas_modo_coherente`),
// y arrastrar un `dia_vencimiento` invisible desde el modo anterior sería un
// rechazo que el operador no puede ver ni explicarse.
function elegirModo (valor) {
  form.periodicidad_tipo = valor
  if (valor === 'CALENDARIO') {
    form.periodicidad_dias = null
    form.dias_hasta_vencimiento = null
    form.periodicidad_preset = form.periodicidad_preset || 'MENSUAL'
    form.dia_vencimiento = form.dia_vencimiento || 10
  } else {
    form.periodicidad_preset = null
    form.dia_vencimiento = null
    form.periodicidad_dias = form.periodicidad_dias || 30
    form.dias_hasta_vencimiento = form.dias_hasta_vencimiento ?? 10
  }
  form.periodo_inicio = ''
}

function alternarAlta () {
  mostrarAlta.value = !mostrarAlta.value
  if (mostrarAlta.value) {
    Object.assign(form, ESTADO_INICIAL)
    tocado.value = false
    error.value = ''
  }
}

function irAlPrimerError () {
  tocado.value = true
  const campo = primerCampoConError(erroresTodos.value, ORDEN_CAMPOS)
  const selector = ANCLAS[campo] ||
    (campo === 'periodo_inicio'
      ? (form.periodicidad_tipo === 'DIAS' ? '#e-inicio-dia' : '#e-inicio')
      : null)
  if (!selector) return
  nextTick(() => {
    const el = document.querySelector(selector)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (typeof el.focus === 'function') el.focus({ preventScroll: true })
  })
}

async function guardar () {
  if (!puedeGuardar.value) { irAlPrimerError(); return }

  guardando.value = true
  error.value = ''
  try {
    // Sólo se mandan los campos del modo elegido; los del otro van en `null`,
    // que es lo que el `CHECK` de coherencia espera.
    const creado = await crearEsquema({
      nombre: form.nombre,
      descripcion: form.descripcion || null,
      periodicidad_tipo: form.periodicidad_tipo,
      periodicidad_preset: form.periodicidad_tipo === 'CALENDARIO' ? form.periodicidad_preset : null,
      dia_vencimiento: form.periodicidad_tipo === 'CALENDARIO' ? form.dia_vencimiento : null,
      periodicidad_dias: form.periodicidad_tipo === 'DIAS' ? form.periodicidad_dias : null,
      dias_hasta_vencimiento: form.periodicidad_tipo === 'DIAS' ? form.dias_hasta_vencimiento : null,
      cantidad_periodos: form.cantidad_periodos,
      periodo_inicio: form.periodo_inicio
    })
    notificaciones.notify({ type: 'success', message: 'Esquema creado.' })
    // Se va al detalle: es donde se ven los períodos REALES y donde se cargan
    // los conceptos, que es lo que falta para que el esquema sirva de algo.
    router.push({
      name: 'EsquemaDetalle',
      params: { id: creado.esquema.cr_esquema_id }
    })
  } catch (err) {
    error.value = err?.userMessage || 'No se pudo crear el esquema.'
  } finally {
    guardando.value = false
  }
}

const abrir = (e) =>
  router.push({ name: 'EsquemaDetalle', params: { id: e.cr_esquema_id } })

async function cargar () {
  cargando.value = true
  errorListado.value = ''
  try {
    esquemas.value = await listarEsquemas({ activo: incluirInactivos.value ? null : true })
  } catch (err) {
    errorListado.value = err?.userMessage || 'Error inesperado al consultar el listado.'
    esquemas.value = []
  } finally {
    cargando.value = false
  }
}

onMounted(cargar)
</script>
